import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { setAuth, getToken } from "@/lib/auth";
import { LOGO_COLLAPSED_DATA } from "@/assets/logoInline";
import {
  storeInviteContinuation,
  clearInviteContinuation,
} from "@/lib/inviteContinuation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // P4-WP-8C.7: client-side only. Never sent, never persisted -- the backend
  // password contract is unchanged.
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // P4-WP-8C.7: the SERVER decides which acceptance experience applies.
  // The client never re-derives it from identity fields.
  const acceptanceFlow = invite?.acceptance_flow === "sign_in" ? "sign_in" : "register";
  const passwordsMatch = password === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;

  const fetchInvite = async () => {
    setLoading(true);
    try {
      const res = await api.get("/invites/lookup", { params: { token } });
      setInvite(res.data);
      setEmail(res.data.email);
      // P4-WP-8C.6: prefill from the inviter's record of who they invited.
      // This is a convenience only -- the field stays editable, and whatever
      // the registrant submits is what becomes the canonical `users.name`.
      // Invitations created before WP-8C.6 carry no names; the field simply
      // stays empty.
      const prefill = [res.data.first_name, res.data.last_name]
        .filter((part) => typeof part === "string" && part.trim())
        .join(" ")
        .trim();
      if (prefill) setName(prefill);
    } catch (e) {
      toast({
        title: "Invite invalid",
        description: e?.response?.data?.detail || "Please contact your admin.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    // P4-WP-8C.7: confirmation is a client-side gate only. It is never added to
    // the request, so the backend contract is byte-for-byte what it was.
    if (!passwordsMatch) return;
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/auth/register", {
        name,
        email,
        password,
        invite_token: token,
      });
      clearInviteContinuation();
      setAuth(res.data.token, res.data.user);
      toast({ title: "Welcome" });
      navigate("/app", { replace: true });
    } catch (err) {
      toast({
        title: "Registration failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // P4-WP-8C.7: the existing-identity half. The token is presented to an
  // AUTHENTICATED endpoint; the server revalidates it and checks that the
  // session's email is the invited one. Browser state authorizes nothing.
  const onClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      await api.post("/auth/invites/claim", { invite_token: token });
      clearInviteContinuation();
      toast({ title: "Invitation accepted" });
      navigate("/app", { replace: true });
    } catch (err) {
      toast({
        title: "Could not accept invitation",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setClaiming(false);
    }
  };

  // Park the token for the round trip through /login, then hand off. The
  // continuation is continuation state only -- the claim above re-proves
  // everything server-side.
  const onSignInToAccept = () => {
    storeInviteContinuation(token);
    navigate("/login", { replace: false });
  };

  return (
    <div className="min-h-screen" data-testid="accept-invite-page">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-5 py-10 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          {/* P4-WP-8C.7: canonical MissionAssist mark, same hierarchy as Login. */}
          <div className="mb-6 flex items-center gap-4">
            {/* P4-WP-8C.7 (logo sizing correction): bounded crop of the asset's own
                transparent padding.

                `LOGO_COLLAPSED_DATA` is a 336x224 canvas whose visible mark
                occupies only left=98 top=67 right=250 bottom=132 -- 152x65, or
                29.0% of the height. A height-driven box therefore renders less
                than a third of itself as ink, which is why `lg:h-28` (112px)
                produced a 32.5px mark. Reaching the intended size by raising
                the height alone would need a ~250px box carrying ~180px of
                empty space, wrecking the auth composition.

                So the wrapper is sized to the VISIBLE aspect (152/65) and the
                image is scaled and offset by percentages derived from that
                bbox, leaving the wrapper filled exactly by the mark:

                  width  336/152 = 221.0526%      left -98/152 = -64.4737%
                  height 224/65  = 344.6154%      top  -67/65  = -103.0769%

                Only transparent padding falls outside the wrapper -- the mark
                itself is never clipped, and the scale stays uniform (the image
                resolves to its native 1.5 aspect), so nothing is distorted.
                The canonical asset is untouched; no transform is used. */}
            <div
              className="relative h-16 overflow-hidden sm:h-[72px] lg:h-20 aspect-[152/65]"
              data-testid="accept-invite-logo"
            >
              <img
                src={LOGO_COLLAPSED_DATA}
                alt="MissionAssist"
                className="absolute max-w-none"
                style={{
                  width: "221.0526%",
                  height: "344.6154%",
                  left: "-64.4737%",
                  top: "-103.0769%",
                }}
              />
            </div>
          </div>
          <h1
            className="text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
            data-testid="accept-invite-title"
          >
            Accept invite
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-200/75" data-testid="accept-invite-subtitle">
            Create your account to join.
          </p>
          <div className="mt-6" data-testid="accept-invite-links">
            <Link
              to="/login"
              className="text-sm text-slate-200/80 underline-offset-4 hover:underline"
              data-testid="accept-invite-back-to-login-link"
            >
              Back to login
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <Card className="w-full max-w-md glass" data-testid="accept-invite-card">
            <CardHeader>
              <CardTitle className="text-slate-100" data-testid="accept-invite-card-title">
                Create account
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="text-sm text-slate-200/70" data-testid="accept-invite-missing-token-text">
                  Missing invite token.
                </div>
              ) : loading ? (
                <div className="text-sm text-slate-200/70" data-testid="accept-invite-loading-text">
                  Loading invite…
                </div>
              ) : !invite ? (
                <div className="text-sm text-slate-200/70" data-testid="accept-invite-invalid-text">
                  Invite invalid.
                </div>
              ) : (
                acceptanceFlow === "sign_in" ? (
                <div className="space-y-4" data-testid="accept-invite-signin-panel">
                  <div className="text-sm text-slate-200/80">
                    <span className="text-slate-100">{invite.email}</span> already has a
                    MissionAssist account. Sign in as that address to join this Account.
                  </div>
                  {getToken() ? (
                    <Button
                      onClick={onClaim}
                      disabled={claiming}
                      className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                      data-testid="accept-invite-claim-button"
                    >
                      {claiming ? "Joining…" : "Accept invitation"}
                    </Button>
                  ) : (
                    <Button
                      onClick={onSignInToAccept}
                      className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                      data-testid="accept-invite-signin-button"
                    >
                      Sign in to accept
                    </Button>
                  )}
                  <div className="text-xs text-slate-400" data-testid="accept-invite-signin-help">
                    Signing in as a different address will not accept this invitation.
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80" data-testid="accept-invite-name-label">
                      Name
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Alex"
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid="accept-invite-name-input"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80" data-testid="accept-invite-email-label">
                      Email
                    </label>
                    {/* P4-WP-8C.7: the invitation was issued to this address
                        and the backend requires it, so editing it could only
                        produce a refusal. readOnly (not disabled) keeps it
                        focusable, announced by screen readers and submitted. */}
                    <Input
                      value={email}
                      readOnly
                      type="email"
                      autoComplete="email"
                      aria-readonly="true"
                      className="bg-white/5 text-slate-300 ring-1 ring-white/10 cursor-not-allowed"
                      data-testid="accept-invite-email-input"
                      required
                    />
                    <div className="text-xs text-slate-400" data-testid="accept-invite-email-help">
                      This invitation was sent to this address.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-slate-200/80" data-testid="accept-invite-password-label">
                      Password
                    </label>
                    <Input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      type="password"
                      autoComplete="new-password"
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid="accept-invite-password-input"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      className="text-sm text-slate-200/80"
                      data-testid="accept-invite-confirm-password-label"
                    >
                      Confirm Password
                    </label>
                    <Input
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      type="password"
                      autoComplete="new-password"
                      aria-invalid={showMismatch ? "true" : "false"}
                      className={`bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ${
                        showMismatch ? "ring-red-400/60" : "ring-white/10"
                      }`}
                      data-testid="accept-invite-confirm-password-input"
                      required
                    />
                    {showMismatch && (
                      <div
                        className="text-xs text-red-300"
                        role="alert"
                        data-testid="accept-invite-password-mismatch"
                      >
                        Passwords do not match.
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !passwordsMatch || !password}
                    className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                    data-testid="accept-invite-submit-button"
                  >
                    {submitting ? "Joining…" : "Join"}
                  </Button>
                </form>
                )
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
